import { useEffect, FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useAppSelector } from 'app/store';
import { getProfileSearchLink } from 'app/store/Profile/selectors';
import { UserInfoCard } from 'app/components/Song/Collaborators/UserInfoCard';
import { getAvatarImage, nameToInitials } from 'app/utils/utils';
import { DDL } from 'types/general.types';
import { SearchableProfile } from 'types/notification.types';
import { ContributionClassName } from 'types/contribution';
import { ContributorSearch } from '../ContributorSearch';
import { getIDFieldInfo, getIsniFieldInfo } from '../RoleFields/utils';
import { ContributionFormData } from '../types';

interface ContributorFieldsProps {
  maxSearchLength: number;
  isExistingUser: boolean;
  contributor?: SearchableProfile | null;
  countriesDDL: DDL;
}

export const ContributorFields: FC<ContributorFieldsProps> = ({
  maxSearchLength,
  isExistingUser,
  contributor,
  countriesDDL,
}) => {
  const {
    register,
    watch,
    control,
    setValue,
  } = useFormContext<ContributionFormData>();

  const profilesSearchUrl = useAppSelector(getProfileSearchLink)?.href || '';
  const watchName = watch('name');

  const resetContributorSearch = () => {
    setValue('contributor', null, { shouldValidate: true });
    setValue('profileGuid', '', { shouldValidate: true });
    setValue('name', '', { shouldValidate: true });
    Object.values(ContributionClassName).forEach((className) => {
      const idField = getIDFieldInfo(className)?.idFieldName;
      const isniField = getIsniFieldInfo(className)?.isniFieldName;
      if (idField) {
        setValue(idField, '', { shouldValidate: true });
      }
      if (isniField) {
        setValue(isniField, '', { shouldValidate: true });
      }
    });
  };

  useEffect(() => {
    register('profileGuid');
  }, [register]);

  return (
    <>
      {contributor?.initials ? (
        <UserInfoCard
          user={{
            displayName: contributor.displayName,
            initials: contributor.initials,
            avatar: getAvatarImage(contributor.profileImages),
            avatarColors: contributor.avatarColors,
          }}
          disableClose={isExistingUser}
          onClose={resetContributorSearch}
        />
      ) : watchName ? (
        <UserInfoCard
          user={{
            displayName: watchName,
            initials: nameToInitials(watchName),
          }}
          onClose={resetContributorSearch}
        />
      ) : (
        <Controller
          name="contributor"
          control={control}
          render={({ field: { onChange, ref } }) => (
            <ContributorSearch
              mainRef={ref}
              maxSearchLength={maxSearchLength}
              countriesDDL={countriesDDL}
              profilesSearchUrl={profilesSearchUrl}
              onChange={onChange}
            />
          )}
        />
      )}
    </>
  );
};
